import type { NewsletterSource } from "@kmstories/shared";
import { useId, useState, type FormEvent } from "react";
import { useT } from "../i18n/LocaleProvider.js";
import { api, ApiError } from "../lib/api.js";
import { useFormToken } from "../lib/useFormToken.js";

/**
 * One field, no name. Double opt-in: the API mails a confirmation; this only
 * ever says "check your inbox". Placed in the hero, the trailer slot and the
 * footer — each instance mints its own token.
 */
export function EmailCapture({ source, buttonLabel, compact = false }: { source: NewsletterSource; buttonLabel?: string; compact?: boolean }) {
  const { m, locale } = useT();
  const { getToken } = useFormToken("newsletter");
  const id = useId();
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "busy" | "done" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    setState("busy");
    setError(null);
    try {
      const formToken = await getToken();
      const hp = form.elements.namedItem("fax") as HTMLInputElement | null;
      await api.post("/api/newsletter", { email, locale, source, formToken, fax: hp?.value ?? "" });
      setState("done");
    } catch (err) {
      setState("error");
      if (err instanceof ApiError && err.code.startsWith("form_token_too_fast")) setError(m.share.errors.tooFast);
      else if (err instanceof ApiError && err.status === 429) setError(m.share.errors.rateLimited);
      else if (err instanceof ApiError && err.status === 400) setError(m.share.errors.emailInvalid);
      else setError(m.common.error);
    }
  }

  if (state === "done") {
    return (
      <p className="notice" role="status">
        {m.newsletter.checkInbox}
      </p>
    );
  }

  return (
    <form className="capture" onSubmit={submit} noValidate>
      <label htmlFor={id} className={compact ? "visually-hidden" : "field__label"}>
        {m.hero.emailLabel}
      </label>
      <div className="capture__row">
        <input
          id={id}
          className="input"
          type="email"
          name="email"
          inputMode="email"
          autoComplete="email"
          dir="ltr"
          placeholder={m.hero.emailPlaceholder}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          aria-invalid={state === "error" ? true : undefined}
          aria-describedby={error ? `${id}-err` : undefined}
        />
        <div className="hp" aria-hidden="true">
          <label>
            Fax
            <input type="text" name="fax" tabIndex={-1} autoComplete="off" defaultValue="" />
          </label>
        </div>
        <button className="btn btn--primary" type="submit" disabled={state === "busy"}>
          {buttonLabel ?? m.hero.emailButton}
        </button>
      </div>
      {error ? (
        <p id={`${id}-err`} className="field__error" role="alert">
          {error}
        </p>
      ) : (
        !compact && <p className="capture__hint">{m.hero.emailHint}</p>
      )}
    </form>
  );
}
